import { useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTheme } from '../contexts/ThemeContext'
import logoSvg from '../assets/images/VE_flashy_4.png'
import shieldImg from '../assets/images/shield.jpg'
import lockImg from '../assets/images/lock.jpg'
import brainImg from '../assets/images/brain.jpg'

// Day Mode Hero - Split layout with logo left, CTA right
const DayHero = () => {
  const videoRef = useRef(null)

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.3
    }
  }, [])

  return (
  <section className="relative min-h-screen overflow-hidden">
    {/* Video background - fixed to cover entire viewport including behind navbar */}
    <video
      ref={videoRef}
      autoPlay
      muted
      loop
      playsInline
      className="fixed inset-0 w-full h-full object-cover -z-10 grayscale"
    >
      <source src="/ve_bg_sbw.mp4" type="video/mp4" />
    </video>
    {/* Pink tint overlay */}
    <div className="fixed inset-0 bg-pink-500/20 mix-blend-color -z-10" />

    {/* Content with navbar padding */}
    <div className="hero-content-wrapper relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">

        {/* Split layout container */}
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12 min-h-[calc(100vh-16rem)]">

          {/* Left side - Logo (1/3 width on desktop) */}
          <div className="w-full lg:w-1/3 flex justify-center lg:justify-start">
            <img
              src={logoSvg}
              alt="VigilantEdge"
              className="w-64 h-64 object-contain"
            />
          </div>

          {/* Right side - CTA (2/3 width on desktop) */}
          <div className="w-full lg:w-2/3">
            <div className="glass-cta-panel text-center lg:text-left">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
                Cybersecurity Assessment Services
              </h1>
              <p className="text-lg md:text-xl mb-6 text-gray-600">
                Protect your digital assets with comprehensive security assessments
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/services" className="btn-glass">
                  View Services
                </Link>
                <Link to="/contact" className="btn-glass-outline">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </section>
  )
}

// Night Mode Hero - Cyberpunk glassmorphism design
const NightHero = () => (
  <section className="relative min-h-screen flex items-center justify-center cyber-bg">
    {/* Cyber overlays */}
    <div className="fixed inset-0 cyber-grid pointer-events-none" />
    <div className="fixed inset-0 scanline pointer-events-none opacity-50" />

    {/* Pulsing decorative dots */}
    <div className="absolute top-1/4 left-20 w-2 h-2 rounded-full bg-cyber-cyan animate-pulse hidden lg:block" />
    <div className="absolute top-1/3 right-32 w-1 h-1 rounded-full bg-cyber-magenta animate-pulse hidden lg:block" />
    <div className="absolute bottom-1/3 left-32 w-1.5 h-1.5 rounded-full bg-cyber-gold animate-pulse hidden lg:block" />

    {/* Glass hero panel */}
    <div className="glass-hero-night max-w-4xl mx-4 lg:mx-auto text-center relative z-10 px-8 py-12">
      {/* Eyebrow */}
      <p className="fade-in-1 text-sm tracking-[0.3em] uppercase mb-8 font-light text-gray-400">
        Next-Gen Security
      </p>

      {/* Headline */}
      <h1 className="fade-in-2 text-5xl md:text-6xl lg:text-7xl font-bold font-orbitron leading-tight mb-6">
        <span className="block text-white">ADVANCED SECURITY</span>
        <span className="block text-cyber-cyan neon-glow-cyan neon-flicker">TESTING</span>
      </h1>

      {/* Divider */}
      <div className="fade-in-2 flex justify-center mb-8">
        <div className="w-48 h-px bg-gradient-to-r from-transparent via-cyber-magenta to-transparent" />
      </div>

      {/* Description */}
      <p className="fade-in-3 text-lg md:text-xl font-light max-w-2xl mx-auto mb-12 leading-relaxed text-gray-400">
        Expert penetration testing and threat analysis. We identify vulnerabilities before attackers do.
      </p>

      {/* CTA Buttons */}
      <div className="fade-in-4 flex flex-col sm:flex-row gap-4 justify-center items-center">
        <Link to="/services" className="btn-neon backdrop-blur-xl">
          View Services
        </Link>
        <Link
          to="/contact"
          className="px-8 py-4 text-sm tracking-widest uppercase border border-gray-600/50 text-gray-300 hover:border-cyber-cyan hover:text-cyber-cyan transition-all backdrop-blur-md bg-void-light/30 rounded-lg"
        >
          Contact Us
        </Link>
      </div>

      {/* Compliance badges */}
      <div className="fade-in-4 mt-16 flex flex-wrap justify-center gap-8 text-xs tracking-wider text-gray-500">
        <span>HIPAA COMPLIANT</span>
        <span className="text-cyber-cyan">|</span>
        <span>SOC 2 CERTIFIED</span>
        <span className="text-cyber-magenta">|</span>
        <span>24/7 MONITORING</span>
      </div>
    </div>

    {/* Scroll indicator */}
    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
      <span className="text-xs tracking-widest uppercase text-gray-500">Scroll</span>
      <div className="w-px h-12 animate-pulse bg-gradient-to-b from-gray-500 to-transparent" />
    </div>
  </section>
)

// Day Mode Features - Glass cards with icons
const DayFeatures = () => (
  <section className="py-16 bg-gradient-to-b from-gray-50 to-gray-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Why Choose VigilantEdge?</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="card text-center group">
          <div className="mb-4 group-hover:scale-110 transition-transform">
            <img
              src={shieldImg}
              alt="Expert Analysis"
              className="w-24 h-24 mx-auto object-cover rounded-lg"
            />
          </div>
          <h3 className="text-xl font-semibold mb-3 text-gray-900">Expert Analysis</h3>
          <p className="text-gray-600">
            Our team of certified security professionals conducts thorough assessments to identify vulnerabilities.
          </p>
        </div>
        <div className="card text-center group">
          <div className="mb-4 group-hover:scale-110 transition-transform">
            <img
              src={lockImg}
              alt="Detailed Reports"
              className="w-24 h-24 mx-auto object-cover rounded-lg"
            />
          </div>
          <h3 className="text-xl font-semibold mb-3 text-gray-900">Detailed Reports</h3>
          <p className="text-gray-600">
            Receive comprehensive reports with actionable insights and remediation recommendations.
          </p>
        </div>
        <div className="card text-center group">
          <div className="mb-4 group-hover:scale-110 transition-transform">
            <img
              src={brainImg}
              alt="Compliance Ready"
              className="w-24 h-24 mx-auto object-cover rounded-lg"
            />
          </div>
          <h3 className="text-xl font-semibold mb-3 text-gray-900">Compliance Ready</h3>
          <p className="text-gray-600">
            Ensure your organization meets industry standards and regulatory requirements.
          </p>
        </div>
      </div>
    </div>
  </section>
)

// Night Mode Features - Glass panels with neon accents
const NightFeatures = () => (
  <section className="py-24 border-t border-cyber-cyan/10 bg-void">
    <div className="max-w-7xl mx-auto px-8">
      <p className="text-xs tracking-[0.4em] uppercase mb-16 text-gray-500">
        OUR SERVICES
      </p>
      <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
        <div className="neon-border-gradient p-6 group">
          <span className="block text-xs mb-3 text-cyber-cyan/70">01</span>
          <h3 className="text-xl font-orbitron mb-3 text-white group-hover:text-cyber-cyan transition-colors">
            Penetration Testing
          </h3>
          <p className="text-sm font-light leading-relaxed text-gray-400">
            Methodical assessment of your defenses with precision.
          </p>
        </div>
        <div className="neon-border-gradient p-6 group">
          <span className="block text-xs mb-3 text-cyber-magenta/70">02</span>
          <h3 className="text-xl font-orbitron mb-3 text-white group-hover:text-cyber-cyan transition-colors">
            Risk Analysis
          </h3>
          <p className="text-sm font-light leading-relaxed text-gray-400">
            Understanding before action. Comprehensive threat modeling.
          </p>
        </div>
        <div className="neon-border-gradient p-6 group">
          <span className="block text-xs mb-3 text-cyber-gold/70">03</span>
          <h3 className="text-xl font-orbitron mb-3 text-white group-hover:text-cyber-cyan transition-colors">
            Compliance
          </h3>
          <p className="text-sm font-light leading-relaxed text-gray-400">
            HIPAA expertise delivered with clarity and discipline.
          </p>
        </div>
      </div>
    </div>
  </section>
)

// Day Mode CTA - Glass panel
const DayCTA = () => (
  <section className="py-16 bg-gradient-to-b from-gray-100 to-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="card text-center max-w-2xl mx-auto">
        <h2 className="text-3xl font-bold mb-6 text-gray-900">Ready to Secure Your Business?</h2>
        <p className="text-xl text-gray-600 mb-8">
          Let's discuss how we can help protect your digital infrastructure.
        </p>
        <Link to="/contact" className="btn-primary">
          Get Started Today
        </Link>
      </div>
    </div>
  </section>
)

// Night Mode CTA - Glass panel with neon border
const NightCTA = () => (
  <section className="py-24 bg-gradient-to-br from-void-light to-void">
    <div className="max-w-4xl mx-auto px-8">
      <div className="neon-border-gradient p-12 text-center">
        <h2 className="text-4xl md:text-5xl font-bold font-orbitron mb-8 text-white">
          SECURE YOUR <span className="text-cyber-cyan neon-glow-cyan">SYSTEMS</span>
        </h2>
        <p className="text-xl mb-12 font-light text-gray-400">
          Partner with our security experts to protect your infrastructure
        </p>
        <Link to="/contact" className="btn-neon backdrop-blur-xl">
          Get Started
        </Link>
      </div>
    </div>
  </section>
)

const Home = () => {
  const { isNight } = useTheme()

  return (
    <div className={isNight ? 'bg-void' : 'bg-transparent'}>
      {isNight ? <NightHero /> : <DayHero />}
      {isNight ? <NightFeatures /> : <DayFeatures />}
      {isNight ? <NightCTA /> : <DayCTA />}
    </div>
  )
}

export default Home
